import { Select, FormLabel, FormHelperText, FormControl, MenuItem, useTheme, Box } from '@mui/material';
import type { SelectProps } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

interface SelectOption {
  id: string | number;
  label: string;
  value: string | number;
}

interface RHFCustomSelectProps extends Omit<SelectProps, 'name'> {
  name: string;
  options?: SelectOption[] | string[];
  outerLabel?: string;
  styleMenu?: React.CSSProperties;
  placeholder?: string;
  onChangeCallback?: (_value: string) => void;
}

export function RHFCustomSelect({
  name,
  options,
  outerLabel,
  styleMenu,
  placeholder = 'Select',
  onChangeCallback,
  required = false,
  sx,
  ...others
}: RHFCustomSelectProps): JSX.Element {
  const { control } = useFormContext();
  const theme: any = useTheme();
  // Normalize options to SelectOption[]
  const normalizedOptions: SelectOption[] = (options || []).map((option) =>
    typeof option === 'string'
      ? { id: option, label: option, value: option }
      : option,
  );

  const menuProps: React.ComponentProps<typeof Select>['MenuProps'] = {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    PaperProps: {
      style: {
        marginTop: 4,
      },
    },
  };

  return (
    <Controller
      control={control}
      name={name}
      defaultValue=""
      render={({ field, fieldState: { error } }) => (
        <FormControl error={Boolean(error)} fullWidth>
          {outerLabel && (
            <FormLabel
              error={false}
              style={{ paddingBottom: '0.6rem', fontSize: 12, color: '#718096' }}>
              {outerLabel}{' '}
              {required && <span style={{ color: '#DC2626', fontSize: 14 }}>*</span>}
            </FormLabel>
          )}
          <Select
            // MUI v6 SelectProps + RHF field spread is overly strict here
            {...({
            displayEmpty: true,
            MenuProps: menuProps,
            style: {
              height: '50px',
              backgroundColor: theme?.palette?.background?.paper || '#fff',
              transition: 'all 0.2s ease',
              fontSize: 12,
            },
            inputRef: field.ref,
            ...field,
            onChange: (e: any) => {
              const selectedValue = e.target.value as string;
              field.onChange(selectedValue);
              if (onChangeCallback) {
                onChangeCallback(selectedValue);
              }
            },
            onBlur: field.onBlur,
            ...others,
            sx: {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme?.palette?.neutral?.[200] || '#e0e0e0',
              },
              '&:hover': {
                backgroundColor: theme?.palette?.neutral?.[50] || '#fafafa',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme?.palette?.neutral?.[300] || '#bdbdbd',
              },
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${(theme?.palette?.primary?.main || '#1976d2')
                  .replace('#', '')
                  .slice(0, 6)}20`,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme?.palette?.primary?.main || '#1976d2',
              },
              '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: ` #DC2626 !important`,
              },
              '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: `  #DC2626 !important`,
              },
              '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: `  #DC2626 !important`,
              },
              ...sx,
            },
            renderValue: (selected: any) => {
              if (!selected) {
                return (
                  <Box
                    component="em"
                    sx={{
                      fontSize: 12,
                      color: (t: any) => t?.palette?.neutral?.[600] || '#999', // always grey
                      fontStyle: 'normal',
                    }}
                  >
                    {placeholder}
                  </Box>
                );
              }
              return (
                normalizedOptions.find((o) => o.value === selected)?.label || selected
              );
            },
            } as any)}
          >
            {/* Do not include the placeholder as a MenuItem */}
            {normalizedOptions.map(({ id, label, value }) => (
              <MenuItem
                value={value}
                key={id}
                style={{
                  fontSize: 12,
                  ...styleMenu,
                }}
                sx={{
                  color: `${theme?.palette?.text?.primary || '#000'} !important`,
                  backgroundColor: 'transparent !important',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: '#F3F4F6 !important',
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: '#F3F4F6 !important',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#F3F4F6 !important',
                    color: `${theme?.palette?.primary?.main || '#1976d2'} !important`,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#E5E7EB !important',
                    },
                  },
                }}>
                {label}
              </MenuItem>
            ))}
          </Select>
          {Boolean(error) && <FormHelperText
            sx={{
              color: '#DC2626 !important',
              ml: 0,
              mr: 0,
              mt: '4px',
              textAlign: 'left',
            }}
          >
            {error?.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
}
